import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import {
  Shield, Buildings, ChartLineUp, Lock, Eye, Warning,
  ArrowCounterClockwise, Users, Handshake, CheckCircle,
  CaretRight, CaretLeft, CaretDown, Check, X, Gear,
  Lightning, ArrowRight, Upload, Robot, FileText, GitBranch,
  Gauge, Target, ListChecks, Sparkle, Info, Cpu,
  PencilSimple, DownloadSimple, Trash, FloppyDisk, List,
  Certificate, Plus, Brain, CircleNotch
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';

// Domain icons mapping for ICT Assessment
const DOMAIN_ICONS = {
  ict_governance: Buildings,
  user_access: Lock,
  change_management: GitBranch,
  ict_operations: Gear,
  security_management: Shield,
  disaster_recovery: ArrowCounterClockwise,
  third_party: Handshake
};

// Score definitions (same as Cyber Diagnostics)
const SCORE_DEFINITIONS = {
  0: { label: 'Not Implemented', shortLabel: 'None', color: 'bg-red-500', textColor: 'text-red-500' },
  1: { label: 'Ad Hoc', shortLabel: 'Ad Hoc', color: 'bg-red-400', textColor: 'text-red-400' },
  2: { label: 'Defined', shortLabel: 'Defined', color: 'bg-amber-500', textColor: 'text-amber-500' },
  3: { label: 'Managed', shortLabel: 'Managed', color: 'bg-lime-500', textColor: 'text-lime-500' },
  4: { label: 'Optimised', shortLabel: 'Optimal', color: 'bg-emerald-500', textColor: 'text-emerald-500' }
};

// Score Selector Component
const ScoreSelector = ({ value, onChange, questionId, disabled }) => (
  <div className="flex gap-1.5" data-testid={`score-selector-${questionId}`}>
    {[0, 1, 2, 3, 4].map((score) => {
      const def = SCORE_DEFINITIONS[score];
      const isSelected = value === score;
      const isRiskFlag = score < 2;
      
      return (
        <button
          key={score}
          data-testid={`score-btn-${questionId}-${score}`}
          onClick={() => !disabled && onChange(questionId, score)}
          disabled={disabled}
          className={`relative flex flex-col items-center p-2 rounded-lg border transition-all min-w-[52px] ${
            isSelected 
              ? `${def.color} border-transparent text-white` 
              : `bg-background border-border hover:border-gold/50 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
          }`}
        >
          <span className={`text-lg font-mono font-bold ${isSelected ? 'text-white' : def.textColor}`}>
            {score}
          </span>
          <span className={`text-[9px] leading-tight ${isSelected ? 'text-white/80' : 'text-text-tertiary'}`}>
            {def.shortLabel}
          </span>
          {isRiskFlag && !isSelected && (
            <Warning size={10} className="absolute -top-1 -right-1 text-amber-500" weight="fill" />
          )}
        </button>
      );
    })}
  </div>
);

// Question Card Component
const QuestionCard = ({ question, answer, onScoreChange, onNotesChange, evidenceStatus, onUploadEvidence, disabled }) => {
  const [showNotes, setShowNotes] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const score = answer?.score;
  const needsEvidence = score !== undefined && score >= 2;
  const hasEvidence = evidenceStatus?.has_evidence;
  const evidenceValidated = evidenceStatus?.status === 'validated';
  const naviResult = evidenceStatus?.navi_result;
  const naviFeedback = evidenceStatus?.feedback;
  const validationPassed = naviResult === 'MEETS';
  
  // Status badge
  const getStatusBadge = () => {
    if (score === undefined) return null;
    
    if (score < 2) {
      return (
        <span className="px-2 py-0.5 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400 flex items-center gap-1">
          <Warning size={10} /> Risk Item
        </span>
      );
    }
    
    if (!hasEvidence) {
      return (
        <span className="px-2 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-400 flex items-center gap-1">
          <Upload size={10} /> Evidence Required
        </span>
      );
    }
    
    if (evidenceValidated && validationPassed) {
      return (
        <span className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-xs text-emerald-400 flex items-center gap-1">
          <CheckCircle size={10} weight="fill" /> Validated
        </span>
      );
    }
    
    if (evidenceValidated && !validationPassed) {
      return (
        <span className="px-2 py-0.5 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400 flex items-center gap-1">
          <Warning size={10} weight="fill" /> Validated - Failed
        </span>
      );
    }
    
    return (
      <span className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-400 flex items-center gap-1">
        <Robot size={10} /> Pending Validation
      </span>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-surface border border-border rounded-lg overflow-hidden"
    >
      {/* Main Question Row */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-400 font-mono">
                {question.id}
              </span>
              {getStatusBadge()}
            </div>
            <p className="text-sm text-text-primary leading-relaxed">{question.question}</p>
            
            {/* ISA 315 Reference */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="mt-2 text-xs text-text-tertiary hover:text-gold flex items-center gap-1"
            >
              <Certificate size={12} />
              {question.regulatory_reference}
              <CaretDown size={12} className={`transition-transform ${showDetails ? 'rotate-180' : ''}`} />
            </button>
          </div>
          
          <ScoreSelector
            value={score}
            onChange={onScoreChange}
            questionId={question.id}
            disabled={disabled}
          />
        </div>
      </div>
      
      {/* Expandable Details */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-border/50 bg-background/50 px-4 py-3"
          >
            <div className="grid md:grid-cols-2 gap-4 text-xs">
              <div>
                <h5 className="font-medium text-text-secondary mb-1">Audit Objective</h5>
                <p className="text-text-tertiary">{question.audit_objective}</p>
              </div>
              <div>
                <h5 className="font-medium text-text-secondary mb-1">Expected Outcome</h5>
                <p className="text-text-tertiary">{question.expected_outcome}</p>
              </div>
              <div className="md:col-span-2">
                <h5 className="font-medium text-text-secondary mb-1">Evidence Required</h5>
                <div className="flex flex-wrap gap-1">
                  {question.evidence_required?.map((ev, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-purple-500/10 border border-purple-500/30 rounded text-purple-400">
                      {ev}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Evidence Upload Section */}
      {needsEvidence && (
        <div className="border-t border-border/50 p-3 bg-background/30">
          <div className="flex items-center justify-between">
            <div className="text-xs text-text-tertiary">
              {hasEvidence ? (
                <span className="flex items-center gap-1">
                  <FileText size={12} className="text-purple-400" />
                  Evidence uploaded
                  {evidenceValidated && (
                    <span className={`ml-1 ${validationPassed ? 'text-emerald-400' : 'text-red-400'}`}>
                      • {validationPassed ? 'Validated' : 'Failed'}
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-amber-400">Evidence required for scores 2-4</span>
              )}
            </div>
            <button
              onClick={() => onUploadEvidence(question.id)}
              className="flex items-center gap-1 px-2 py-1 bg-purple-500/10 border border-purple-500/30 rounded text-xs text-purple-400 hover:bg-purple-500/20 transition-colors"
            >
              <Upload size={12} />
              {hasEvidence ? 'Add More' : 'Upload'}
            </button>
          </div>
          
          {/* NAVI Feedback */}
          {evidenceValidated && naviFeedback && (
            <div className={`mt-3 p-3 rounded-lg text-xs ${
              validationPassed ? 'bg-emerald-500/5 border border-emerald-500/20' : 'bg-red-500/5 border border-red-500/20'
            }`}>
              <div className="flex items-start gap-2">
                <Robot size={14} className={validationPassed ? 'text-emerald-400' : 'text-red-400'} />
                <div>
                  <p className={`font-medium ${validationPassed ? 'text-emerald-400' : 'text-red-400'}`}>NAVI Feedback:</p>
                  <p className="text-text-secondary mt-1">{naviFeedback}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Notes Toggle */}
      <div className="border-t border-border/50 px-3 py-2">
        <button
          onClick={() => setShowNotes(!showNotes)}
          className="text-xs text-text-tertiary hover:text-text-secondary flex items-center gap-1"
        >
          <PencilSimple size={12} />
          {showNotes ? 'Hide Notes' : 'Add Notes'}
        </button>
        
        {showNotes && (
          <textarea
            value={answer?.notes || ''}
            onChange={(e) => onNotesChange(question.id, e.target.value)}
            placeholder="Add assessment notes..."
            className="w-full mt-2 p-2 bg-background border border-border rounded text-sm text-text-primary resize-none"
            rows={2}
          />
        )}
      </div>
    </motion.div>
  );
};

// Evidence Upload Modal
const EvidenceUploadModal = ({ isOpen, onClose, questionId, assessmentId, onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);
  
  if (!isOpen) return null;
  
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('question_id', questionId);
    
    try {
      const response = await axios.post(
        `${API}/ict-assessment/assessments/${assessmentId}/upload-evidence`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.success) {
        setUploadedFiles(prev => [...prev, {
          id: response.data.evidence_id,
          filename: file.name,
          status: 'pending',
          naviFeedback: null
        }]);
        toast.success('Evidence uploaded');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  const handleAnalyze = async (evidenceId) => {
    setAnalyzing(true);
    
    const formData = new FormData();
    formData.append('question_id', questionId);
    formData.append('evidence_id', evidenceId);
    
    try {
      const response = await axios.post(
        `${API}/ict-assessment/assessments/${assessmentId}/analyze-evidence`,
        formData
      );
      
      if (response.data.success) {
        const result = response.data.navi_result;
        const feedback = response.data.navi_feedback;
        
        setUploadedFiles(prev => prev.map(f => 
          f.id === evidenceId ? { 
            ...f, 
            status: 'analyzed', 
            naviResult: result,
            naviFeedback: feedback,
            validationPassed: result === 'MEETS'
          } : f
        ));
        
        onUploadComplete(questionId, { 
          status: 'validated', 
          navi_result: result, 
          has_evidence: true,
          feedback: feedback,
          validation_passed: result === 'MEETS'
        });
      }
    } catch (error) {
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-surface border border-border rounded-lg w-full max-w-lg"
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h3 className="font-medium text-text-primary">Upload Evidence</h3>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileUpload}
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
            />
            <Upload size={32} className="mx-auto text-text-tertiary mb-2" />
            <p className="text-sm text-text-secondary mb-2">Upload supporting documentation</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-4 py-2 bg-gold/10 border border-gold/30 rounded text-gold text-sm hover:bg-gold/20 disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : 'Select File'}
            </button>
          </div>
          
          {/* Uploaded Files */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-3">
              {uploadedFiles.map((file) => (
                <div key={file.id} className="bg-background rounded-lg border border-border overflow-hidden">
                  <div className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={16} className="text-purple-400 flex-shrink-0" />
                      <span className="text-sm text-text-primary truncate">{file.filename}</span>
                    </div>
                    {file.status === 'pending' ? (
                      <button
                        onClick={() => handleAnalyze(file.id)}
                        disabled={analyzing}
                        className="flex items-center gap-1 px-2 py-1 bg-gold/10 text-gold text-xs rounded hover:bg-gold/20 disabled:opacity-50 flex-shrink-0"
                      >
                        {analyzing ? 'Analyzing...' : (
                          <>
                            <Robot size={12} />
                            Validate with NAVI
                          </>
                        )}
                      </button>
                    ) : (
                      <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                        file.naviResult === 'MEETS' 
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' 
                          : 'bg-amber-500/10 text-amber-400 border border-amber-500/30'
                      }`}>
                        {file.naviResult === 'MEETS' ? (
                          <>
                            <CheckCircle size={12} weight="fill" />
                            Validated
                          </>
                        ) : (
                          <>
                            <Warning size={12} weight="fill" />
                            Review Needed
                          </>
                        )}
                      </span>
                    )}
                  </div>
                  
                  {file.status === 'analyzed' && file.naviFeedback && (
                    <div className={`px-3 pb-3 pt-0`}>
                      <div className={`p-3 rounded-lg text-xs ${
                        file.naviResult === 'MEETS'
                          ? 'bg-emerald-500/5 border border-emerald-500/20'
                          : 'bg-amber-500/5 border border-amber-500/20'
                      }`}>
                        <p className="text-text-secondary">{file.naviFeedback}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="p-4 border-t border-border flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gold text-gold-foreground rounded text-sm font-medium hover:bg-gold-hover"
          >
            Done
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Main ICT Assessment Page
const ICTAssessmentPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [domains, setDomains] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [currentAssessment, setCurrentAssessment] = useState(null);
  const [activeDomain, setActiveDomain] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [newAssessmentName, setNewAssessmentName] = useState('');
  const [newOrgName, setNewOrgName] = useState('');
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  const [naviAnalysis, setNaviAnalysis] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  
  // Fetch domains and questions
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [domainsRes, questionsRes, assessmentsRes] = await Promise.all([
          axios.get(`${API}/ict-assessment/domains`),
          axios.get(`${API}/ict-assessment/questions`),
          axios.get(`${API}/ict-assessment/assessments`)
        ]);
        
        setDomains(domainsRes.data.domains);
        setQuestions(questionsRes.data.questions);
        setAssessments(assessmentsRes.data.assessments);
        
        if (domainsRes.data.domains.length > 0) {
          setActiveDomain(domainsRes.data.domains[0].id);
        }
        
        // Load most recent assessment if exists
        if (assessmentsRes.data.assessments.length > 0) {
          const latestAssessment = assessmentsRes.data.assessments[0];
          const fullAssessment = await axios.get(`${API}/ict-assessment/assessments/${latestAssessment.id}`);
          setCurrentAssessment(fullAssessment.data.assessment);
        }
      } catch (error) {
        console.error('Failed to load ICT assessment data:', error);
        toast.error('Failed to load assessment data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Create new assessment
  const createAssessment = async () => {
    if (!newAssessmentName.trim()) {
      toast.error('Please enter an assessment name');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/ict-assessment/assessments`, {
        name: newAssessmentName,
        organization_name: newOrgName || null
      });
      
      if (response.data.success) {
        setCurrentAssessment(response.data.assessment);
        setAssessments(prev => [response.data.assessment, ...prev]);
        setShowCreateModal(false);
        setNewAssessmentName('');
        setNewOrgName('');
        toast.success('Assessment created');
      }
    } catch (error) {
      toast.error('Failed to create assessment');
    }
  };
  
  // Update answer
  const handleScoreChange = async (questionId, score) => {
    if (!currentAssessment) return;
    
    try {
      const response = await axios.post(
        `${API}/ict-assessment/assessments/${currentAssessment.id}/answer`,
        { question_id: questionId, score }
      );
      
      if (response.data.success) {
        setCurrentAssessment(prev => ({
          ...prev,
          answers: { ...prev.answers, [questionId]: score },
          overall_score: response.data.overall_score,
          maturity_level: response.data.maturity_level
        }));
      }
    } catch (error) {
      toast.error('Failed to save answer');
    }
  };
  
  // Update notes
  const handleNotesChange = async (questionId, notes) => {
    if (!currentAssessment) return;
    
    setCurrentAssessment(prev => ({
      ...prev,
      answer_notes: { ...prev.answer_notes, [questionId]: notes }
    }));
  };
  
  // Handle evidence upload
  const handleUploadEvidence = (questionId) => {
    setSelectedQuestionId(questionId);
    setShowEvidenceModal(true);
  };
  
  // Handle evidence upload completion
  const handleEvidenceComplete = (questionId, validationData) => {
    setCurrentAssessment(prev => ({
      ...prev,
      evidence_validations: {
        ...prev.evidence_validations,
        [questionId]: validationData
      }
    }));
  };
  
  // Save & Analyze with NAVI
  const handleSaveAndAnalyze = async () => {
    if (!currentAssessment) return;
    
    const answeredCount = Object.keys(currentAssessment.answers || {}).length;
    if (answeredCount === 0) {
      toast.error('Please answer at least one question before saving');
      return;
    }
    
    setSavingAnalysis(true);
    try {
      const response = await axios.post(
        `${API}/ict-assessment/assessments/${currentAssessment.id}/save-and-analyze`
      );
      
      if (response.data.success) {
        setNaviAnalysis(response.data.navi_analysis);
        setCurrentAssessment(prev => ({
          ...prev,
          navi_analysis: response.data.navi_analysis,
          overall_score: response.data.overall_score,
          maturity_level: response.data.maturity_level,
          status: 'analyzed'
        }));
        setShowAnalysisModal(true);
        toast.success(`Assessment saved! ${response.data.risk_items_logged} risk items logged.`);
      }
    } catch (error) {
      console.error('Save and analyze error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save and analyze');
    } finally {
      setSavingAnalysis(false);
    }
  };
  
  // Get questions for active domain
  const domainQuestions = questions.filter(q => q.domain === activeDomain);
  
  // Calculate domain progress
  const getDomainProgress = (domainId) => {
    if (!currentAssessment) return { answered: 0, total: 0, percentage: 0 };
    const domainQs = questions.filter(q => q.domain === domainId);
    const answered = domainQs.filter(q => currentAssessment.answers?.[q.id] !== undefined).length;
    return {
      answered,
      total: domainQs.length,
      percentage: domainQs.length > 0 ? Math.round(answered / domainQs.length * 100) : 0
    };
  };
  
  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="ml-64 flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="ml-64 flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border bg-surface flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading font-semibold text-text-primary flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Cpu size={24} className="text-blue-400" />
                </div>
                ICT Assessment
              </h1>
              <p className="text-sm text-text-secondary mt-1">
                IT General Controls Assessment aligned to ISA 315
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {currentAssessment && (
                <div className="text-right">
                  <p className="text-xs text-text-tertiary">Overall Score</p>
                  <p className="text-2xl font-mono font-bold text-gold">
                    {currentAssessment.overall_score?.toFixed(1) || 0}%
                  </p>
                  <p className="text-xs text-text-secondary">{currentAssessment.maturity_level || 'Not Started'}</p>
                </div>
              )}
              
              {currentAssessment && (
                <button
                  onClick={handleSaveAndAnalyze}
                  disabled={savingAnalysis}
                  data-testid="save-analyze-btn"
                  className="px-4 py-2 bg-emerald-600 text-white rounded-sm font-medium flex items-center gap-2 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingAnalysis ? (
                    <>
                      <CircleNotch size={18} className="animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Brain size={18} />
                      Save & Analyze
                    </>
                  )}
                </button>
              )}
              
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-gold text-gold-foreground rounded-sm font-medium flex items-center gap-2 hover:bg-gold-hover"
              >
                <Plus size={18} />
                New Assessment
              </button>
            </div>
          </div>
        </div>
        
        {!currentAssessment ? (
          /* No Assessment State */
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center max-w-md">
              <div className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Cpu size={40} className="text-blue-400" />
              </div>
              <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">
                Start Your ICT Assessment
              </h2>
              <p className="text-text-secondary mb-6">
                Assess your organisation's IT General Controls across 7 domains aligned to ISA 315.
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-gold text-gold-foreground rounded-sm font-medium hover:bg-gold-hover"
              >
                Create Assessment
              </button>
            </div>
          </div>
        ) : (
          /* Assessment Interface */
          <div className="flex-1 flex overflow-hidden">
            {/* Domain Sidebar */}
            <div className="w-72 bg-surface border-r border-border flex-shrink-0 overflow-y-auto">
              <div className="p-4">
                <p className="text-xs text-text-tertiary uppercase tracking-wider mb-3">Assessment Domains</p>
                <div className="space-y-1">
                  {domains.map((domain) => {
                    const IconComponent = DOMAIN_ICONS[domain.id] || Shield;
                    const progress = getDomainProgress(domain.id);
                    const isActive = activeDomain === domain.id;
                    
                    return (
                      <button
                        key={domain.id}
                        onClick={() => setActiveDomain(domain.id)}
                        className={`w-full p-3 rounded-lg text-left transition-all ${
                          isActive 
                            ? 'bg-gold/10 border border-gold/30' 
                            : 'hover:bg-background border border-transparent'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            isActive ? 'bg-gold/20' : 'bg-blue-500/10'
                          }`}>
                            <IconComponent size={18} className={isActive ? 'text-gold' : 'text-blue-400'} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isActive ? 'text-gold' : 'text-text-primary'}`}>
                              {domain.name}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${progress.percentage === 100 ? 'bg-emerald-500' : 'bg-gold'}`}
                                  style={{ width: `${progress.percentage}%` }}
                                />
                              </div>
                              <span className="text-[10px] text-text-tertiary">
                                {progress.answered}/{progress.total}
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            
            {/* Questions Area */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeDomain && (
                <>
                  <div className="mb-6">
                    <h2 className="text-lg font-heading font-semibold text-text-primary">
                      {domains.find(d => d.id === activeDomain)?.name}
                    </h2>
                    <p className="text-sm text-text-secondary mt-1">
                      {domains.find(d => d.id === activeDomain)?.description}
                    </p>
                    <p className="text-xs text-blue-400 mt-2">
                      <Certificate size={14} className="inline mr-1" />
                      {domains.find(d => d.id === activeDomain)?.isa_315_ref}
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    {domainQuestions.map((question) => (
                      <QuestionCard
                        key={question.id}
                        question={question}
                        answer={{
                          score: currentAssessment.answers?.[question.id],
                          notes: currentAssessment.answer_notes?.[question.id]
                        }}
                        onScoreChange={handleScoreChange}
                        onNotesChange={handleNotesChange}
                        evidenceStatus={currentAssessment.evidence_validations?.[question.id]}
                        onUploadEvidence={handleUploadEvidence}
                        disabled={false}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Create Assessment Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-surface border border-border rounded-lg w-full max-w-md"
          >
            <div className="p-4 border-b border-border">
              <h3 className="font-medium text-text-primary">Create New ICT Assessment</h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-1">Assessment Name *</label>
                <input
                  type="text"
                  value={newAssessmentName}
                  onChange={(e) => setNewAssessmentName(e.target.value)}
                  placeholder="e.g., Q1 2026 ITGC Review"
                  className="w-full px-3 py-2 bg-background border border-border rounded text-text-primary"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Organisation Name</label>
                <input
                  type="text"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  placeholder="e.g., ABC Municipality"
                  className="w-full px-3 py-2 bg-background border border-border rounded text-text-primary"
                />
              </div>
            </div>
            <div className="p-4 border-t border-border flex justify-end gap-2">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-text-secondary hover:text-text-primary"
              >
                Cancel
              </button>
              <button
                onClick={createAssessment}
                className="px-4 py-2 bg-gold text-gold-foreground rounded font-medium hover:bg-gold-hover"
              >
                Create
              </button>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Evidence Upload Modal */}
      <EvidenceUploadModal
        isOpen={showEvidenceModal}
        onClose={() => setShowEvidenceModal(false)}
        questionId={selectedQuestionId}
        assessmentId={currentAssessment?.id}
        onUploadComplete={handleEvidenceComplete}
      />
      
      {/* NAVI Analysis Modal */}
      {showAnalysisModal && naviAnalysis && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-surface border border-gold/30 rounded-lg max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col"
          >
            <div className="p-4 border-b border-border flex items-center justify-between bg-gradient-to-r from-emerald-500/10 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <Brain size={24} className="text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-text-primary">NAVI Analysis Complete</h3>
                  <p className="text-xs text-text-tertiary">ICT Assessment - {currentAssessment?.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowAnalysisModal(false)}
                className="p-2 hover:bg-background rounded"
              >
                <X size={20} className="text-text-tertiary" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {/* Score Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-background rounded-lg p-4 text-center">
                  <p className="text-xs text-text-tertiary mb-1">Overall Score</p>
                  <p className="text-3xl font-mono font-bold text-gold">
                    {currentAssessment?.overall_score?.toFixed(0) || 0}%
                  </p>
                </div>
                <div className="bg-background rounded-lg p-4 text-center">
                  <p className="text-xs text-text-tertiary mb-1">Maturity Level</p>
                  <p className="text-lg font-semibold text-emerald-400">
                    {currentAssessment?.maturity_level || 'N/A'}
                  </p>
                </div>
                <div className="bg-background rounded-lg p-4 text-center">
                  <p className="text-xs text-text-tertiary mb-1">Risk Items</p>
                  <p className="text-3xl font-mono font-bold text-red-400">
                    {currentAssessment?.risk_count || 0}
                  </p>
                </div>
              </div>
              
              {/* NAVI Analysis Content */}
              <div className="prose prose-invert prose-sm max-w-none">
                <div className="bg-background rounded-lg p-4 border border-border">
                  <div className="whitespace-pre-wrap text-text-secondary text-sm leading-relaxed">
                    {naviAnalysis}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t border-border flex justify-between items-center">
              <Link
                to="/risk-log"
                className="text-sm text-gold hover:underline flex items-center gap-1"
              >
                <Warning size={16} />
                View Risk Log
              </Link>
              <div className="flex gap-2">
                <Link
                  to="/themes/cybersecurity"
                  className="px-4 py-2 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 text-sm flex items-center gap-2"
                >
                  <ChartLineUp size={16} />
                  View Dashboard
                </Link>
                <button
                  onClick={() => setShowAnalysisModal(false)}
                  className="px-4 py-2 bg-gold text-gold-foreground rounded font-medium hover:bg-gold-hover text-sm"
                >
                  Done
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ICTAssessmentPage;
